// D80 Tier 2 — In-note AI block (TipTap Node + React NodeView).
// Renders a card with a prompt input + 4 actions:
//   • Draft       — generate fresh content from the prompt
//   • Continue    — continue writing the current note
//   • Summarize   — bullet-point summary of the current note
//   • Ask         — answer a question using the note as context
// On success, the AI block replaces itself with the generated content
// rendered as TipTap blocks (paragraphs / headings / lists / quotes).
//
// Contracts are NEVER exposed to non-owners: the backend AI-assist
// endpoint strips contract-marked nodes from the context before the
// LLM call, and contract entities are excluded from the input
// payload here too (no contract data is ever attached on the
// frontend side).

import { Node } from '@tiptap/core';
import { ReactNodeViewRenderer, NodeViewWrapper } from '@tiptap/react';
import { useState } from 'react';
import { Sparkles, Wand2, FileText, MessageCircle, X, Loader2 } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Convert simple Markdown-ish text from the LLM into TipTap blocks.
// We keep this small — headings (#, ##, ###), bullets, numbered,
// quotes, dividers, paragraphs. Bold/italic via **…**, *…*.
function textToTipTapBlocks(text) {
  const lines = (text || '').split(/\r?\n/);
  const blocks = [];
  let curList = null; // {type:'bulletList'|'orderedList', items:[]}
  const flushList = () => {
    if (curList) {
      blocks.push({ type: curList.type, content: curList.items });
      curList = null;
    }
  };
  const parseInline = (s) => {
    // Very small subset: **bold**, *italic*, `code`.
    const out = [];
    let i = 0; let buf = '';
    const push = (text, marks) => {
      if (!text) return;
      out.push(marks?.length ? { type: 'text', text, marks } : { type: 'text', text });
    };
    while (i < s.length) {
      if (s[i] === '*' && s[i + 1] === '*') {
        const end = s.indexOf('**', i + 2);
        if (end > -1) {
          push(buf); buf = '';
          push(s.slice(i + 2, end), [{ type: 'bold' }]);
          i = end + 2; continue;
        }
      } else if (s[i] === '*') {
        const end = s.indexOf('*', i + 1);
        if (end > -1) {
          push(buf); buf = '';
          push(s.slice(i + 1, end), [{ type: 'italic' }]);
          i = end + 1; continue;
        }
      } else if (s[i] === '`') {
        const end = s.indexOf('`', i + 1);
        if (end > -1) {
          push(buf); buf = '';
          push(s.slice(i + 1, end), [{ type: 'code' }]);
          i = end + 1; continue;
        }
      }
      buf += s[i]; i++;
    }
    push(buf);
    return out;
  };
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) { flushList(); continue; }
    let m;
    if ((m = line.match(/^(#{1,3})\s+(.*)$/))) {
      flushList();
      blocks.push({ type: 'heading', attrs: { level: m[1].length },
        content: parseInline(m[2]) });
    } else if ((m = line.match(/^[-*]\s+(.*)$/))) {
      if (!curList || curList.type !== 'bulletList') {
        flushList(); curList = { type: 'bulletList', items: [] };
      }
      curList.items.push({ type: 'listItem', content: [
        { type: 'paragraph', content: parseInline(m[1]) },
      ]});
    } else if ((m = line.match(/^\d+\.\s+(.*)$/))) {
      if (!curList || curList.type !== 'orderedList') {
        flushList(); curList = { type: 'orderedList', items: [] };
      }
      curList.items.push({ type: 'listItem', content: [
        { type: 'paragraph', content: parseInline(m[1]) },
      ]});
    } else if (line === '---') {
      flushList(); blocks.push({ type: 'horizontalRule' });
    } else if (line.startsWith('> ')) {
      flushList();
      blocks.push({ type: 'blockquote', content: [
        { type: 'paragraph', content: parseInline(line.slice(2)) },
      ]});
    } else {
      flushList();
      blocks.push({ type: 'paragraph', content: parseInline(line) });
    }
  }
  flushList();
  return blocks.length ? blocks : [{ type: 'paragraph' }];
}

function AiBlockNodeView(props) {
  const { editor, getPos, deleteNode, extension } = props;
  const workspaceId = extension.options?.workspaceId || '';
  const [prompt, setPrompt] = useState('');
  const [mode, setMode] = useState('draft');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const runAi = async (chosenMode) => {
    setError('');
    setLoading(true);
    try {
      const token = localStorage.getItem('stellar_token');
      const noteContent = editor.getJSON();
      const r = await fetch(`${BACKEND}/api/notes/ai-assist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json',
                   ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          workspace_id: workspaceId,
          mode: chosenMode,
          prompt,
          note_content: noteContent,
        }),
      });
      if (!r.ok) {
        const txt = await r.text().catch(() => '');
        throw new Error(`AI failed (${r.status}): ${txt.slice(0, 160)}`);
      }
      const data = await r.json();
      const blocks = textToTipTapBlocks(data.text || '');
      // Replace this AI block with the generated content.
      const pos = getPos();
      if (typeof pos === 'number') {
        editor.chain().focus()
          .insertContentAt({ from: pos, to: pos + 1 }, blocks)
          .run();
      } else {
        deleteNode();
        editor.chain().focus().insertContent(blocks).run();
      }
    } catch (e) {
      setError(String(e.message || e));
      setLoading(false);
    }
  };

  return (
    <NodeViewWrapper
      as="div"
      data-type="ai-block"
      className="stellar-ai-block"
      data-testid="ai-block"
    >
      <div className="stellar-ai-head">
        <Sparkles size={14} className="text-gold" />
        <span>AI assist</span>
        <button
          type="button" onMouseDown={(e) => e.preventDefault()}
          onClick={() => deleteNode()}
          className="stellar-ai-close" aria-label="Remove AI block"
          data-testid="ai-block-close"
        ><X size={12} /></button>
      </div>
      <textarea
        value={prompt}
        onChange={(e) => { setPrompt(e.target.value); setMode('draft'); }}
        placeholder="Ask for a draft, summary, or any question…"
        className="stellar-ai-prompt"
        rows={2}
        data-testid="ai-block-prompt"
      />
      {error && <div className="stellar-ai-error" data-testid="ai-block-error">{error}</div>}
      <div className="stellar-ai-actions">
        <button
          type="button" onMouseDown={(e) => e.preventDefault()}
          disabled={loading} onClick={() => runAi('draft')}
          className="stellar-ai-btn primary" data-testid="ai-action-draft"
        >
          {loading && mode === 'draft' ? <Loader2 size={12} className="animate-spin" /> : <Wand2 size={12} />}
          Draft
        </button>
        <button
          type="button" onMouseDown={(e) => e.preventDefault()}
          disabled={loading} onClick={() => runAi('continue')}
          className="stellar-ai-btn" data-testid="ai-action-continue"
        >Continue writing</button>
        <button
          type="button" onMouseDown={(e) => e.preventDefault()}
          disabled={loading} onClick={() => runAi('summarize')}
          className="stellar-ai-btn" data-testid="ai-action-summarize"
        >
          <FileText size={12} />
          Summarize note
        </button>
        <button
          type="button" onMouseDown={(e) => e.preventDefault()}
          disabled={loading || !prompt.trim()}
          onClick={() => runAi('ask')}
          className="stellar-ai-btn" data-testid="ai-action-ask"
        >
          <MessageCircle size={12} />
          Ask
        </button>
      </div>
    </NodeViewWrapper>
  );
}

export const AiBlock = Node.create({
  name: 'aiBlock',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: false,
  addOptions() { return { workspaceId: '' }; },
  addAttributes() { return {}; },
  parseHTML() {
    return [{ tag: 'div[data-type="ai-block"]' }];
  },
  renderHTML() {
    return ['div', { 'data-type': 'ai-block', class: 'stellar-ai-block' }];
  },
  addNodeView() {
    return ReactNodeViewRenderer(AiBlockNodeView);
  },
  addCommands() {
    return {
      insertAiBlock: () => ({ commands }) =>
        commands.insertContent({ type: this.name }),
    };
  },
});
